import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AdminStatsGrid from './components/admin/AdminStatsGrid';
import AdminDataOpsToolbar from './components/admin/AdminDataOpsToolbar';
import LibraryTab from './components/admin/LibraryTab';
import ScansTab from './components/admin/ScansTab';
import UsersTab from './components/admin/UsersTab';
import SacrificePoolPanel from './components/admin/SacrificePoolPanel';
import AdminAiPanel from './components/admin-ai/AdminAiPanel';
import BarcodeDetailModal from './components/admin/BarcodeDetailModal';

import useAdminData from './hooks/useAdminData';
import useBarcodeDetailEditor from './hooks/useBarcodeDetailEditor';

/**
 * Admin shell: authorises access, composes hooks, routes data into presentational tabs.
 * Shrunk from 460→~120 lines by moving data fetching + mutation into dedicated hooks
 * (`useAdminData`, `useBarcodeDetailEditor`).
 */
export default function AdminLibrary() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get('tab') === 'ai' ? 'ai' : 'library'));

  const {
    loading,
    unauthorized,
    stats,
    library,
    scans,
    users,
    search,
    setSearch,
    actionRunning,
    actionResult,
    fetchJSON,
    loadAll,
    runAction,
  } = useAdminData();

  const editor = useBarcodeDetailEditor({ fetchJSON, onAfterMutation: loadAll });

  // Guards
  if (loading) return <div className="min-h-screen bg-zinc-950 text-zinc-200 p-8">Loading admin…</div>;
  if (unauthorized)
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-200 p-8" data-testid="admin-forbidden">
        <h1 className="text-2xl font-bold text-red-400">403 — Admin only</h1>
        <p className="mt-2 text-zinc-400">
          Your account is not the configured admin email. Set <code>ADMIN_EMAIL</code> in backend/.env.
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-6 px-4 py-2 bg-zinc-800 rounded"
        >
          Back to dashboard
        </button>
      </div>
    );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-200 p-6" data-testid="admin-library-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Master Library — Admin</h1>
            <p className="text-zinc-400 text-sm">
              Read-only view of barcodes, scans, and users. Live from MongoDB.
            </p>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded"
            data-testid="admin-back-btn"
          >
            ← Dashboard
          </button>
        </div>

        <AdminStatsGrid stats={stats} />

        <div className="flex border-b border-zinc-800 mb-4">
          {['library', 'scans', 'users', 'sacrifice', 'ai'].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 text-sm font-medium ${
                tab === t
                  ? 'text-[#00D26A] border-b-2 border-[#00D26A]'
                  : 'text-zinc-400 hover:text-zinc-200'
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t === 'library' && `Library (${library.total})`}
              {t === 'scans' && `Scans (${scans.total})`}
              {t === 'users' && `Users (${users.total})`}
              {t === 'sacrifice' && 'Sacrifice Pool'}
              {t === 'ai' && 'AI Assistant'}
            </button>
          ))}
          <button
            onClick={loadAll}
            className="ml-auto px-3 py-2 text-sm text-zinc-400 hover:text-zinc-200"
            data-testid="admin-refresh-btn"
          >
            ↻ Refresh
          </button>
        </div>

        <AdminDataOpsToolbar
          actionRunning={actionRunning}
          actionResult={actionResult}
          runAction={runAction}
        />

        {tab === 'library' && (
          <LibraryTab
            library={library}
            search={search}
            setSearch={setSearch}
            onSearchSubmit={loadAll}
            onRowClick={editor.openDetail}
          />
        )}
        {tab === 'scans' && <ScansTab scans={scans} />}
        {tab === 'users' && <UsersTab users={users} />}
        {tab === 'sacrifice' && <SacrificePoolPanel />}
        {tab === 'ai' && <AdminAiPanel />}

        <BarcodeDetailModal
          selectedBarcode={editor.selectedBarcode}
          detail={editor.detail}
          editing={editor.editing}
          editValue={editor.editValue}
          savingEdit={editor.savingEdit}
          setEditValue={editor.setEditValue}
          setEditing={editor.setEditing}
          onSave={editor.saveEdit}
          onDelete={editor.deleteEntry}
          onClose={editor.close}
        />
      </div>
    </div>
  );
}
